/**
 * Insurance Connector Wrapper
 *
 * Handles full modal, minimized loading, and result overlays.
 * Uses fixed positioning throughout to stay anchored to the viewport.
 */

"use client";

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@frontend/shared/components/ui/drawer";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, X } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { InsuranceConnectorContent } from "./InsuranceConnectorContent";
import {
  useInsuranceConnectorState,
  useInsuranceConnectorSend,
  useNavButtonRef,
} from "./insurance-connector-provider";
import { ConfirmDisconnect } from "./components/ConfirmDisconnect";
import { InsuranceMenu } from "./components/InsuranceMenu";
import { MinimizedLoadingBox } from "./components/MinimizedLoadingBox";
import { SuccessConfirmation } from "./components/SuccessConfirmation";
import { UnverifiedResult } from "./components/UnverifiedResult";
import { InactiveResult } from "./components/InactiveResult";
import { AllProvidersOonResult } from "./components/AllProvidersOonResult";
import { OutOfNetworkResult } from "./components/OutOfNetworkResult";
import { getStepConfig } from "./step-config";

function getMinimizedSubState(stateValue: unknown): string | null {
  if (
    typeof stateValue === "object" &&
    stateValue !== null &&
    "minimized" in stateValue
  ) {
    const sub = (stateValue as Record<string, unknown>).minimized;
    return typeof sub === "string" ? sub : null;
  }
  return null;
}

/**
 * Scrolls a focused input inside the drawer's scroll container above the
 * virtual keyboard.
 *
 * iOS Safari's native "scroll focused input into view" only scrolls the
 * window; a `position: fixed` drawer doesn't participate in window scroll,
 * so the input stays covered by the keyboard. This hook walks the scroll
 * container (the element that owns `overflow-y-auto`) and shifts its
 * `scrollTop` so the input sits above the visual viewport's bottom edge.
 */
function useScrollInputAboveKeyboard(
  containerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const vv = window.visualViewport;
    if (!vv) return;

    let activeInput: HTMLElement | null = null;
    let rafId = 0;

    const BOTTOM_BUFFER = 24;

    const scrollIfCovered = () => {
      if (!activeInput) return;
      const rect = activeInput.getBoundingClientRect();
      const visibleBottom = vv.offsetTop + vv.height;
      const overlap = rect.bottom - (visibleBottom - BOTTOM_BUFFER);
      if (overlap > 0) {
        container.scrollBy({ top: overlap, behavior: "smooth" });
      }
    };

    const scheduleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setTimeout(scrollIfCovered, 200);
      });
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.matches("input, textarea, select")) return;
      activeInput = target;
      scheduleScroll();
    };

    const handleFocusOut = () => {
      activeInput = null;
    };

    container.addEventListener("focusin", handleFocusIn);
    container.addEventListener("focusout", handleFocusOut);
    vv.addEventListener("resize", scheduleScroll);
    vv.addEventListener("scroll", scheduleScroll);

    return () => {
      container.removeEventListener("focusin", handleFocusIn);
      container.removeEventListener("focusout", handleFocusOut);
      vv.removeEventListener("resize", scheduleScroll);
      vv.removeEventListener("scroll", scheduleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [containerRef, enabled]);
}

export function InsuranceConnectorWrapper() {
  const state = useInsuranceConnectorState();
  const send = useInsuranceConnectorSend();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navButtonRef = useNavButtonRef();
  const t = useTranslations("InsuranceConnector");
  const [showDrawer, setShowDrawer] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const uiMode = state?.context?.uiMode ?? "closed";
  const minimizedSubState = getMinimizedSubState(state?.value);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  useScrollInputAboveKeyboard(mobileScrollRef, isMobile && uiMode === "full");

  const isResultState = useMemo(
    () =>
      !!state &&
      (state.matches({ minimized: "successConfirmation" }) ||
        state.matches({ minimized: "unverifiedResult" }) ||
        state.matches({ minimized: "unverifiedSecondResult" }) ||
        state.matches({ minimized: "inactiveResult" }) ||
        state.matches({ minimized: "outOfNetworkResult" }) ||
        state.matches({ minimized: "allProvidersOonResult" }) ||
        state.matches({ minimized: "insuranceMenu" }) ||
        state.matches({ minimized: "confirmDisconnect" })),
    [state],
  );

  const isLoadingState = useMemo(
    () => !!state && state.matches({ minimized: "loadingInsurance" }),
    [state],
  );

  const isMinimized = uiMode === "minimized";

  useEffect(() => {
    if (!isMinimized || !navButtonRef?.current) return;

    const measure = () => {
      if (navButtonRef.current) {
        const rect = navButtonRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setAnchorRect(rect);
        }
      }
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [isMinimized, navButtonRef]);

  // Manage mobile drawer
  useEffect(() => {
    if (isMobile) {
      setShowDrawer(uiMode === "full");
    }
  }, [uiMode, isMobile]);

  // Lock background scroll while the full modal is open on every "main"
  // screen (intro through results). Uses the iOS-safe position:fixed pattern
  // so Safari can't rubber-band the page behind the overlay, and locks both
  // <html> and <body> in case scroll lives on documentElement.
  useEffect(() => {
    if (uiMode !== "full") return;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyTouchAction: body.style.touchAction,
      bodyOverscroll: body.style.overscrollBehavior,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.touchAction = "none";
    body.style.overscrollBehavior = "none";

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.touchAction = previous.bodyTouchAction;
      body.style.overscrollBehavior = previous.bodyOverscroll;
      window.scrollTo(0, scrollY);
    };
  }, [uiMode]);

  if (!state || !state.context || uiMode === "closed") {
    return null;
  }

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      send({ type: "CLOSE" });
    }
  };

  const hasValidAnchor =
    anchorRect && anchorRect.width > 0 && anchorRect.height > 0;
  const anchorStyle = {
    top: hasValidAnchor ? anchorRect.bottom + 12 : 80,
    right: hasValidAnchor
      ? Math.max(0, window.innerWidth - anchorRect.right)
      : 16,
  };

  const fullState = state.matches("full")
    ? (state.value as { full: string }).full
    : "";
  const currentStepConfig = getStepConfig(fullState);
  const eligibilityStatesWithBack = new Set([
    "eligibilityBirthdayInput",
    "eligibilityStateQuestion",
    "workplaceInjuryQuestion",
  ]);
  const showMobileBackButton =
    currentStepConfig?.showBackButton ||
    eligibilityStatesWithBack.has(fullState);

  // ── Mobile ──
  if (isMobile) {
    return (
      <>
        {uiMode === "full" && (
          <Drawer
            open={showDrawer}
            onOpenChange={handleDrawerOpenChange}
            repositionInputs={false}
            shouldScaleBackground={false}
          >
            <DrawerContent
              className="rounded-t-3xl flex flex-col p-6"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(100px)",
                WebkitBackdropFilter: "blur(100px)",
                boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
                height: "90dvh",
                maxHeight: "90dvh",
              }}
            >
              <DrawerTitle className="sr-only">
                {t("InsuranceConnectorTitle")}
              </DrawerTitle>
              <div className="flex justify-between items-center flex-shrink-0">
                {showMobileBackButton ? (
                  <button
                    type="button"
                    onClick={() => send({ type: "BACK" })}
                    className="border border-black rounded-full aspect-square w-10 h-10 flex items-center justify-center"
                    aria-label="Go back"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                ) : (
                  <div />
                )}
                <button
                  type="button"
                  onClick={() => send({ type: "CLOSE" })}
                  className="rounded-sm transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={t("Close")}
                >
                  <X className="h-8 w-8" strokeWidth={1.25} />
                </button>
              </div>
              <div
                ref={mobileScrollRef}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain"
              >
                <InsuranceConnectorContent />
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {uiMode === "minimized" && (
          <AnimatePresence mode="wait">
            {isLoadingState && (
              <motion.div
                key="loading-mobile"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[99]"
                onClick={() => send({ type: "CLOSE" })}
                role="presentation"
              >
                <motion.div
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="fixed top-16 left-0 right-0 z-[100] mx-4 rounded-3xl"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(100px)",
                    WebkitBackdropFilter: "blur(100px)",
                    boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MinimizedLoadingBox isMobile />
                </motion.div>
              </motion.div>
            )}
            {isResultState && (
              <motion.div
                key={`result-mobile-${minimizedSubState}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[99]"
                onClick={() => send({ type: "CLOSE" })}
                role="presentation"
              >
                <motion.div
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="fixed top-16 left-0 right-0 z-[100] mx-4 rounded-3xl"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(100px)",
                    WebkitBackdropFilter: "blur(100px)",
                    boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderMinimizedResult(state, true)}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </>
    );
  }

  // ── Desktop ──
  return (
    <>
      {/* Full-screen form modal */}
      {uiMode === "full" && (
        <div
          role="presentation"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              send({ type: "CLOSE" });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              send({ type: "CLOSE" });
            }
          }}
        >
          <div
            className="relative rounded-[32px] flex flex-col overflow-hidden shadow-2xl"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(100px)",
              WebkitBackdropFilter: "blur(100px)",
              boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
              width: 700,
              height: 600,
            }}
          >
            <div className="flex justify-end pt-12 px-12">
              <button
                type="button"
                onClick={() => send({ type: "CLOSE" })}
                className="rounded-sm transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label={t("Close")}
              >
                <X className="h-8 w-8" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-12 pb-12">
              <InsuranceConnectorContent />
            </div>
          </div>
        </div>
      )}

      {/* Minimized states — anchored below the nav button */}
      {uiMode === "minimized" && (
        <AnimatePresence mode="wait">
          {isLoadingState && (
            <motion.div
              key="minimized-loading"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="fixed z-[100] rounded-3xl"
              style={{
                ...anchorStyle,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(100px)",
                WebkitBackdropFilter: "blur(100px)",
                boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
              }}
            >
              <MinimizedLoadingBox />
            </motion.div>
          )}

          {isResultState && (
            <motion.div
              key={`minimized-${minimizedSubState}`}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="fixed z-[100] rounded-3xl"
              style={{
                ...anchorStyle,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(100px)",
                WebkitBackdropFilter: "blur(100px)",
                boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.15)",
              }}
            >
              {renderMinimizedResult(state)}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
}

function renderMinimizedResult(
  state: {
    matches: (v: string | Record<string, unknown>) => boolean;
  },
  isMobile = false,
) {
  if (state.matches({ minimized: "insuranceMenu" })) {
    return <InsuranceMenu isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "confirmDisconnect" })) {
    return <ConfirmDisconnect isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "successConfirmation" })) {
    return <SuccessConfirmation isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "unverifiedResult" })) {
    return <UnverifiedResult isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "unverifiedSecondResult" })) {
    return <UnverifiedResult isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "inactiveResult" })) {
    return <InactiveResult isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "outOfNetworkResult" })) {
    return <OutOfNetworkResult isMobile={isMobile} />;
  }
  if (state.matches({ minimized: "allProvidersOonResult" })) {
    return <AllProvidersOonResult isMobile={isMobile} />;
  }
  return null;
}
